import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Table,
  useToast,
} from "../../design-system";
import PortalLayout from "./PortalLayout";
import useInspectionPortal from "./useInspectionPortal";
import { formatTimeHHMM } from "../../helpers/datetime";

export default function PortalJornadas() {
  document.title = "Inspección · Jornadas | Kronaly";
  return (
    <PortalLayout>
      <Body />
    </PortalLayout>
  );
}

function Body() {
  const { http, workspace } = useInspectionPortal();
  const tz = workspace?.timezone;
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const initialEmployee = searchParams.get("employee_id") || "";

  const today = useMemo(() => isoYmd(new Date()), []);
  const firstOfMonth = useMemo(() => {
    const d = new Date();
    return isoYmd(new Date(d.getFullYear(), d.getMonth(), 1));
  }, []);

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [employeeId, setEmployeeId] = useState(initialEmployee);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null); // formato actual
  const [lastSignature, setLastSignature] = useState(null);

  // Cargar lista de empleados para el selector
  useEffect(() => {
    let mounted = true;
    http
      .get("/inspection/employees")
      .then((r) => {
        if (!mounted) return;
        setEmployees(r?.data?.data || r?.data || []);
      })
      .catch(() => setEmployees([]));
    return () => {
      mounted = false;
    };
  }, [http]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (employeeId) params.employee_id = employeeId;
      const r = await http.get("/inspection/workdays", { params });
      setRows(r?.data?.data || r?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err?.message || "Error al cargar jornadas.");
    } finally {
      setLoading(false);
    }
  }, [http, startDate, endDate, employeeId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const handleDownload = async (format) => {
    setDownloading(format);
    try {
      const params = { start_date: startDate, end_date: endDate, format };
      if (employeeId) params.employee_id = employeeId;
      const r = await http.get("/inspection/export", {
        params,
        responseType: format === "json" ? "json" : "blob",
      });

      let blob;
      let signatureId = null;

      // El backend devuelve el signature_id en el header X-Inspection-Signature-Id
      // (expuesto vía CORS). Para JSON también está dentro del body — preferimos
      // el header por uniformidad.
      const headerSig = r?.headers?.["x-inspection-signature-id"]
        || r?.headers?.get?.("x-inspection-signature-id");
      if (headerSig) signatureId = headerSig;

      if (format === "json") {
        const payload = r?.data;
        if (!signatureId) signatureId = payload?.manifest?.signature_id || null;
        blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      } else {
        blob = r?.data;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inspection-fichajes-${startDate}_${endDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      if (signatureId) setLastSignature(signatureId);
      toast.success(
        "Descarga completada",
        signatureId
          ? `Signature ID: ${signatureId.slice(0, 8)}… (guárdalo para verificar)`
          : "Archivo descargado. El manifest con el signature_id está dentro.",
      );
    } catch (err) {
      toast.error(
        "No se pudo descargar",
        err?.response?.data?.error?.message || err?.message || "Error desconocido",
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Jornadas en el periodo"
        description="Consulta los fichajes y descarga la información firmada en el formato que prefieras."
      />

      <div style={filterRow}>
        <Field label="Desde">
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Hasta">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
        <Field label="Empleado">
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name}{e.is_active ? "" : " (baja)"}
              </option>
            ))}
          </select>
        </Field>
        <Button variant="primary" icon="RefreshCw" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Buscar"}
        </Button>
      </div>

      <div style={downloadRow}>
        <span style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>
          Descargar como documento firmado:
        </span>
        <Button
          variant="primary"
          icon="FileText"
          onClick={() => handleDownload("pdf")}
          disabled={downloading !== null}
        >
          {downloading === "pdf" ? "Generando…" : "PDF"}
        </Button>
        <Button
          variant="secondary"
          icon="Download"
          onClick={() => handleDownload("csv")}
          disabled={downloading !== null}
        >
          {downloading === "csv" ? "Generando…" : "CSV"}
        </Button>
        <Button
          variant="ghost"
          icon="Code"
          onClick={() => handleDownload("json")}
          disabled={downloading !== null}
        >
          {downloading === "json" ? "Generando…" : "JSON"}
        </Button>
      </div>

      {lastSignature && (
        <Alert tone="success">
          <strong>Último export firmado:</strong>{" "}
          <code style={{ fontSize: 12 }}>{lastSignature}</code>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Guarda este Signature ID. Puedes verificar la integridad del archivo descargado en cualquier
            momento desde la pestaña "Verificar export".
          </div>
        </Alert>
      )}

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="Clock"
          title="Sin jornadas en el rango"
          description="Ningún empleado tiene fichajes registrados en el periodo seleccionado. Cambia el rango o el empleado y vuelve a buscar."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <th style={th}>Fecha</th>
              <th style={th}>Empleado</th>
              <th style={th}>Estado</th>
              <th style={th}>Entradas / Salidas</th>
              <th style={th}>Pausas</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.workday_id} style={!w.is_active ? { background: "var(--kr-bg-subtle)" } : undefined}>
                <td style={td}>{w.date}</td>
                <td style={td}>{w.employee_name || "—"}</td>
                <td style={td}>
                  <Badge tone={w.is_active ? "neutral" : "danger"}>{w.status}</Badge>
                  {!w.is_active && (
                    <div style={{ fontSize: 10, color: "var(--kr-danger-700, #b91c1c)", marginTop: 2 }}>
                      Eliminada
                    </div>
                  )}
                </td>
                <td style={td}>
                  {w.segments.length === 0 ? (
                    <span style={{ color: "var(--kr-fg-muted)" }}>—</span>
                  ) : (
                    w.segments.map((s) => (
                      <div key={s.id} style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                        {formatTime(s.clock_in_at, tz)} → {s.clock_out_at ? formatTime(s.clock_out_at, tz) : "en curso"}
                        {!s.is_active && (
                          <span style={{ color: "var(--kr-danger-700, #b91c1c)", marginLeft: 6, fontSize: 10 }}>
                            (eliminado)
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </td>
                <td style={td}>
                  {w.breaks.length === 0 ? (
                    <span style={{ color: "var(--kr-fg-muted)" }}>—</span>
                  ) : (
                    w.breaks.map((b, i) => (
                      <div key={b.id || i} style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                        {formatTime(b.start_at, tz)} – {formatTime(b.end_at, tz)}
                      </div>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

function isoYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Instante en la zona del workspace de inspección.
function formatTime(iso, tz) {
  return formatTimeHHMM(iso, tz);
}

const th = {
  textAlign: "left",
  padding: "10px 16px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "top",
};

const filterRow = {
  display: "flex",
  gap: 12,
  alignItems: "flex-end",
  flexWrap: "wrap",
  marginBottom: 12,
};

const downloadRow = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 16,
  padding: 12,
  background: "var(--kr-bg-subtle)",
  borderRadius: 6,
};

const selectStyle = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--kr-border)",
  background: "var(--kr-bg)",
  color: "var(--kr-fg)",
  fontSize: 14,
  minWidth: 200,
};
